import is from '@sindresorhus/is';

import type { JobLevelDetails } from '~/shared/data-contract/job-level-details';
import { getCompetencyTitle, type JobProfileComponent } from '~/shared/data-contract/job-profile';
import type { JobRoleDetails } from '~/shared/data-contract/job-role-details';
import { getSkillTitle, type JobProfileSkill } from '~/shared/data-contract/skill';
import type { LanguageCodeForJSA } from '~/shared/internationalization';

import { pickProfileTaskTranslation } from '../profile-tasks/api/profile-task-transport-adapter';

type ProfileSection = NonNullable<JobProfileComponent['structured']>[number];

// Both job roles and job levels carry the same profile payload, so one comparison builder serves both.
export type ComparedProfileDetails = JobLevelDetails | JobRoleDetails;

export type ProfileSectionComparisonCell = {
    readonly itemId: string;
    readonly value: string | undefined;
};

export type ProfileSectionComparisonRow = {
    readonly cells: readonly ProfileSectionComparisonCell[];
    readonly id: string;
    readonly sectionId: string | undefined;
    readonly tag: string;
};

export type SkillComparisonCell = {
    readonly isRequired: boolean;
    readonly itemId: string;
    readonly proficiency: JobProfileSkill['level'];
};

export type SkillComparisonRow = {
    readonly cells: readonly SkillComparisonCell[];
    readonly competencyId: string | undefined;
    readonly competencyTitle: string | undefined;
    readonly id: string;
    readonly skillType: JobProfileSkill['skillType'];
    readonly title: string;
};

export type ProfileTaskComparisonEntry = {
    readonly description: string | undefined;
    readonly id: string;
    readonly taskVersionId: string;
    readonly title: string;
};

export type ProfileTaskComparisonColumn = {
    readonly itemId: string;
    readonly tasks: readonly ProfileTaskComparisonEntry[];
};

type SkillComparisonDescriptor = {
    readonly competencyId: string | undefined;
    readonly competencyTitle: string | undefined;
    readonly skill: JobProfileSkill;
};

const addSkillComparisonDescriptor = (
    skillsById: Map<string, SkillComparisonDescriptor>,
    skill: JobProfileSkill,
    competencyTitles: ReadonlyMap<string, string>,
): void => {
    const competencyId = skill.competencyId;
    const competencyTitle = competencyId === undefined ? undefined : competencyTitles.get(competencyId);
    const existing = skillsById.get(skill.id);

    if (existing === undefined) {
        skillsById.set(skill.id, {
            competencyId,
            competencyTitle,
            skill,
        });

        return;
    }

    if (existing.competencyTitle === undefined && competencyTitle !== undefined) {
        skillsById.set(skill.id, {
            ...existing,
            competencyId: competencyId ?? existing.competencyId,
            competencyTitle,
        });
    }
};

const buildCompetencyTitleMap = (
    items: readonly ComparedProfileDetails[],
    language: LanguageCodeForJSA,
): ReadonlyMap<string, string> => {
    const competencyTitles = new Map<string, string>();

    for (const item of items) {
        for (const [competencyId, competency] of item.jobProfile?.competencies ?? []) {
            if (!competencyTitles.has(competencyId)) {
                competencyTitles.set(competencyId, getCompetencyTitle(competency, language));
            }
        }
    }

    return competencyTitles;
};

const getSkillSortGroup = (row: Pick<SkillComparisonRow, 'competencyId' | 'skillType'>): number => {
    if (row.skillType === 'language') {
        return 2;
    }

    return row.competencyId === undefined ? 0 : 1;
};

export const getTranslatedJobItemTitle = (
    item: Pick<ComparedProfileDetails, 'jobProfile' | 'title' | 'translations'>,
    language: LanguageCodeForJSA,
): string =>
    item.jobProfile?.translations?.find((translation) => translation.language === language)?.title ??
    item.translations.find((translation) => translation.language === language)?.title ??
    item.title;

const getProfileSections = (item: ComparedProfileDetails, language: LanguageCodeForJSA): readonly ProfileSection[] => {
    const profile = item.jobProfile;

    return (
        profile?.translations.find((translation) => translation.language === language)?.structured ??
        profile?.structured ??
        []
    );
};

const matchesSection = (
    candidate: Pick<ProfileSection, 'id' | 'tag'>,
    section: Pick<ProfileSection, 'id' | 'tag'>,
): boolean =>
    (!is.undefined(candidate.id) && !is.undefined(section.id) && candidate.id === section.id) ||
    candidate.tag === section.tag;

const itemHasStructuredTasks = (item: ComparedProfileDetails): boolean => (item.jobProfile?.tasks?.length ?? 0) > 0;

const hasStructuredTasks = (items: readonly ComparedProfileDetails[]): boolean => items.some(itemHasStructuredTasks);

// The backend synthesizes one structured element per task into TASKS-type template sections. When a
// dedicated tasks group renders those tasks, the synthesized elements must not show up twice.
const isTaskSectionTag = (sectionTag: { readonly type: string }): boolean => sectionTag.type === 'TASKS';

export const buildProfileSectionComparison = (
    items: readonly ComparedProfileDetails[],
    language: LanguageCodeForJSA,
    { excludeTaskSections = false }: { readonly excludeTaskSections?: boolean } = {},
): readonly ProfileSectionComparisonRow[] => {
    // Only a profile that has structured tasks drops its task section; a legacy profile in the same
    // comparison keeps its plain task text, since the tasks group has nothing to show for it.
    const dropsTaskSections = (item: ComparedProfileDetails): boolean =>
        excludeTaskSections && itemHasStructuredTasks(item);
    const taskSectionIdsByItem = items.map(
        (item) => new Set((item.jobProfile?.sectionTags ?? []).filter(isTaskSectionTag).map(({ id }) => id)),
    );
    const isTaskSectionOf = (itemIndex: number, section: Pick<ProfileSection, 'id' | 'tag'>): boolean =>
        (taskSectionIdsByItem[itemIndex]?.has(section.id ?? '') ?? false) || section.tag === 'tasks';
    const sectionsByItem = items.map((item, itemIndex) =>
        getProfileSections(item, language).filter(
            (section) => !(dropsTaskSections(item) && isTaskSectionOf(itemIndex, section)),
        ),
    );
    const allSections = sectionsByItem.flat();
    const sectionDescriptors: Array<Pick<ProfileSection, 'id' | 'tag'>> = [];

    const addSection = (section: Pick<ProfileSection, 'id' | 'tag'>): void => {
        if (!sectionDescriptors.some((candidate) => matchesSection(candidate, section))) {
            sectionDescriptors.push(section);
        }
    };

    for (const item of items) {
        for (const sectionTag of item.jobProfile?.sectionTags ?? []) {
            if (dropsTaskSections(item) && isTaskSectionTag(sectionTag)) {
                continue;
            }

            const matchingSection = allSections.find((section) => section.id === sectionTag.id);

            addSection({
                id: sectionTag.id,
                tag: matchingSection?.tag ?? `section_${sectionTag.id.replaceAll('-', '_')}`,
            });
        }
    }

    for (const section of allSections) {
        addSection(section);
    }

    return sectionDescriptors.map((section) => ({
        cells: items.map((item, itemIndex) => ({
            itemId: item.id,
            value: sectionsByItem[itemIndex]?.find((candidate) => matchesSection(candidate, section))?.value,
        })),
        id: section.id ?? `tag:${section.tag}`,
        sectionId: section.id,
        tag: section.tag,
    }));
};

export const buildSkillComparison = (
    items: readonly ComparedProfileDetails[],
    language: LanguageCodeForJSA,
): readonly SkillComparisonRow[] => {
    const skillsById = new Map<string, SkillComparisonDescriptor>();
    const competencyTitles = buildCompetencyTitleMap(items, language);
    const skillsByItem = items.map(
        (item) => new Map(item.jobProfile?.skills?.map((skill) => [skill.id, skill] as const)),
    );

    for (const item of items) {
        for (const skill of item.jobProfile?.skills ?? []) {
            addSkillComparisonDescriptor(skillsById, skill, competencyTitles);
        }
    }

    return [...skillsById.values()]
        .map(({ competencyId, competencyTitle, skill }) => ({
            cells: items.map((item, itemIndex) => {
                const itemSkill = skillsByItem[itemIndex]?.get(skill.id);

                return {
                    isRequired: !is.undefined(itemSkill),
                    itemId: item.id,
                    proficiency: itemSkill?.level,
                };
            }),
            competencyId,
            competencyTitle,
            id: skill.id,
            skillType: skill.skillType,
            title: getSkillTitle(skill, language),
        }))
        .sort((left, right) => {
            const groupComparison = getSkillSortGroup(left) - getSkillSortGroup(right);
            if (groupComparison !== 0) {
                return groupComparison;
            }

            const leftCompetency = left.competencyTitle ?? '';
            const rightCompetency = right.competencyTitle ?? '';
            const competencyComparison = leftCompetency.localeCompare(rightCompetency, language);

            return competencyComparison === 0 ? left.title.localeCompare(right.title, language) : competencyComparison;
        });
};

// Tasks are never aligned across items: two profiles do not share task ids, so each column lists its own.
export const buildProfileTaskComparison = (
    items: readonly ComparedProfileDetails[],
    language: LanguageCodeForJSA,
): readonly ProfileTaskComparisonColumn[] =>
    items.map((item) => ({
        itemId: item.id,
        tasks: [...(item.jobProfile?.tasks ?? [])]
            .sort((left, right) => left.order - right.order)
            .map((task) => {
                const translation = pickProfileTaskTranslation(task, language);

                return {
                    description: translation?.description ?? task.description,
                    id: task.taskId,
                    taskVersionId: String(task.taskVersion),
                    title: translation?.title ?? task.title ?? '',
                };
            }),
    }));

export { hasStructuredTasks };
